import React, { useState } from 'react';
import { Box, Typography, Chip, Stack, Accordion, AccordionSummary, AccordionDetails, Checkbox, FormControlLabel, IconButton, TextField, Button, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SchoolIcon from '@mui/icons-material/School';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { normalizeLearningOutcomesData } from '../../utils/normalizeLearningOutcomes';

const BLOOM_COLORS = {
  Remember: '#94a3b8',
  Understand: '#60a5fa',
  Apply: '#34d399',
  Analyze: '#fbbf24',
  Evaluate: '#f97316',
  Create: '#ec4899',
  Unknown: '#9ca3af'
};

const OUTCOME_TYPE_LABELS = {
  ELO: 'Exit Level Outcome',
  SpecificOutcome: 'Specific Outcome',
  KM: 'Knowledge Module',
  PSM: 'Practical Skills Module',
  WEM: 'Workplace Experience Module'
};

export default function OutcomesDisplay({ data, onEdit, userEdited, isOrientation = false }) {
  const normalizedData = normalizeLearningOutcomesData(data);
  const outcomes = normalizedData?.outcomes || [];
  const [editingField, setEditingField] = useState(null); // 'core_message', 'supporting_0', 'supporting_1', etc.
  const [editedValue, setEditedValue] = useState('');
  const [editedData, setEditedData] = useState(data);
  
  if (!outcomes || outcomes.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No outcomes available
        </Typography>
      </Box>
    );
  }

  // Show user edited indicator
  const showUserEditedPill = userEdited || data?.user_edited;
  
  // Start editing a field
  const startEdit = (fieldKey, currentValue) => {
    setEditingField(fieldKey);
    setEditedValue(currentValue);
    setEditedData(data);
  };
  
  // Save edited field
  const saveEdit = (outcomeIndex, field, value) => {
    const newOutcomes = [...editedData.outcomes];
    
    if (field === 'core_message') {
      newOutcomes[outcomeIndex].core_message = value;
    } else if (field.startsWith('supporting_')) {
      const idx = parseInt(field.split('_')[1]);
      newOutcomes[outcomeIndex].supporting_points[idx] = value;
    } else if (field === 'statement') {
      newOutcomes[outcomeIndex].statement = value;
    } else if (field === 'bloom_level') {
      newOutcomes[outcomeIndex].bloom_level = value;
    } else if (field.startsWith('assessment_criteria_')) {
      const idx = parseInt(field.split('_')[2]);
      if (!newOutcomes[outcomeIndex].assessment_criteria) {
        newOutcomes[outcomeIndex].assessment_criteria = [];
      }
      newOutcomes[outcomeIndex].assessment_criteria[idx] = value;
    }
    
    const newData = { ...editedData, outcomes: newOutcomes };
    if (typeof onEdit === 'function') {
      onEdit(newData);
    }
    setEditingField(null);
    setEditedValue('');
  };
  
  // Cancel editing
  const cancelEdit = () => {
    setEditingField(null);
    setEditedValue('');
  };

  // Core Outcomes have a different structure: { core_message, supporting_points, evidence.nodes, etc. }
  const firstOutcome = outcomes[0];
  const hasCoreMessage = !!firstOutcome?.core_message;

  // Render Core Outcomes format (core_message + supporting_points)
  if (hasCoreMessage) {
    return (
      <Box sx={{ p: 2 }}>
        {showUserEditedPill && (
          <Box sx={{ mb: 2 }}>
            <Chip 
              label="✏️ User Edited" 
              size="small" 
              color="info" 
              sx={{ fontWeight: 600 }}
            />
          </Box>
        )}
        {outcomes.map((outcome, idx) => (
          <Box key={idx}>
            {/* Core Message - Main Statement */}
            <Box sx={{ mb: 3, p: 3, bgcolor: '#f0f9ff', borderRadius: 2, border: '2px solid #3b82f6' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e40af' }}>
                  Core Message
                </Typography>
                {editingField !== `core_message_${idx}` && (
                  <IconButton 
                    size="small" 
                    onClick={() => startEdit(`core_message_${idx}`, outcome.core_message)}
                    sx={{ color: '#3b82f6' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              
              {editingField === `core_message_${idx}` ? (
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={editedValue}
                    onChange={(e) => setEditedValue(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ mb: 2, bgcolor: 'white' }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={() => saveEdit(idx, 'core_message', editedValue)}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={cancelEdit}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Box>
              ) : (
                <Typography variant="body1" sx={{ color: '#1e3a8a', lineHeight: 1.7 }}>
                  {outcome.core_message}
                </Typography>
              )}
              
              {outcome.confidence && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Confidence: {Math.round(outcome.confidence * 100)}%
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Supporting Points */}
            {outcome.supporting_points && outcome.supporting_points.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
                  Supporting Learning Points ({outcome.supporting_points.length})
                </Typography>
                {outcome.supporting_points.map((point, i) => (
                  <Box key={i} sx={{ mb: 1.5, pl: 2, borderLeft: '3px solid #3b82f6', position: 'relative' }}>
                    {editingField === `supporting_${idx}_${i}` ? (
                      <Box>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          value={editedValue}
                          onChange={(e) => setEditedValue(e.target.value)}
                          variant="outlined"
                          size="small"
                          sx={{ mb: 1, bgcolor: 'white' }}
                        />
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={() => saveEdit(idx, `supporting_${i}`, editedValue)}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={cancelEdit}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Typography variant="body2" sx={{ color: '#475569', flex: 1 }}>
                          {point}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => startEdit(`supporting_${idx}_${i}`, point)}
                          sx={{ ml: 1, color: '#64748b' }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}

            {/* Evidence Nodes - Hidden for orientation nodes */}
            {!isOrientation && (outcome['evidence.nodes'] || outcome.evidence_nodes) && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                  Evidence Nodes
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {(outcome['evidence.nodes'] || outcome.evidence_nodes || []).map((node, i) => (
                    <Chip 
                      key={i} 
                      label={node} 
                      size="small" 
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }} 
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Alignment Paradigms */}
            {outcome.alignment?.paradigms && outcome.alignment.paradigms.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                  Alignment Paradigms
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {outcome.alignment.paradigms.map((paradigm, i) => (
                    <Chip 
                      key={i} 
                      label={paradigm.replace(/_/g, ' ')} 
                      size="small" 
                      sx={{ bgcolor: '#dbeafe', color: '#1e40af', textTransform: 'capitalize' }} 
                    />
                  ))}
                </Stack>
              </Box>
            )}

            {/* Collapsible Sections */}
            {outcome.implications && outcome.implications.length > 0 && (
              <Accordion sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Implications ({outcome.implications.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {outcome.implications.map((impl, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 1, color: '#475569' }}>
                      • {impl}
                    </Typography>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            {outcome.risks && outcome.risks.length > 0 && (
              <Accordion sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#dc2626' }}>
                    Risks ({outcome.risks.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {outcome.risks.map((risk, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 1, color: '#991b1b' }}>
                      ⚠️ {risk}
                    </Typography>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            {outcome.unknowns && outcome.unknowns.length > 0 && (
              <Accordion sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ca8a04' }}>
                    Unknowns ({outcome.unknowns.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {outcome.unknowns.map((unknown, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 1, color: '#713f12' }}>
                      ❓ {unknown}
                    </Typography>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}

            {outcome.assumptions && outcome.assumptions.length > 0 && (
              <Accordion sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Assumptions ({outcome.assumptions.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {outcome.assumptions.map((assumption, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 1, color: '#475569' }}>
                      💭 {assumption}
                    </Typography>
                  ))}
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        ))}
      </Box>
    );
  }

  // Fallback: Render standard outcomes format (for Learning Outcomes)
  const grouped = outcomes.reduce((acc, outcome) => {
    const type = outcome.type || 'Unknown';
    if (!acc[type]) acc[type] = [];
    acc[type].push(outcome);
    return acc;
  }, {});

  return (
    <Box sx={{ p: 2 }}>
      {showUserEditedPill && (
        <Box sx={{ mb: 2 }}>
          <Chip 
            label="✏️ User Edited" 
            size="small" 
            color="info" 
            sx={{ fontWeight: 600 }}
          />
        </Box>
      )}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
        Learning Outcomes ({outcomes.length})
      </Typography>
      
      {Object.entries(grouped).map(([type, typeOutcomes]) => (
        <Box key={type} sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b', display: 'block', mb: 1 }}>
            {OUTCOME_TYPE_LABELS[type] || type}
          </Typography>
          {typeOutcomes.map((outcome, idx) => {
            const globalIdx = outcomes.findIndex(o => o === outcome);
            return (
              <Accordion key={idx} sx={{ mb: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: '#fafafa' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    {editingField === `statement_${globalIdx}` ? (
                      <TextField
                        fullWidth
                        multiline
                        value={editedValue}
                        onChange={(e) => setEditedValue(e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{ bgcolor: 'white' }}
                        autoFocus
                      />
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                        {outcome.statement || outcome.description || 'Outcome ' + (idx + 1)}
                      </Typography>
                    )}
                    {editingField !== `statement_${globalIdx}` && (
                      <Box
                        component="div"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(`statement_${globalIdx}`, outcome.statement || outcome.description || '');
                        }}
                        sx={{ 
                          ml: 'auto',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 32,
                          height: 32,
                          cursor: 'pointer',
                          borderRadius: '50%',
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                          color: 'inherit'
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </Box>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    {editingField === `statement_${globalIdx}` && (
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          onClick={() => saveEdit(globalIdx, 'statement', editedValue)}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          onClick={cancelEdit}
                        >
                          Cancel
                        </Button>
                      </Stack>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {editingField === `bloom_level_${globalIdx}` ? (
                        <TextField
                          select
                          value={editedValue}
                          onChange={(e) => setEditedValue(e.target.value)}
                          variant="outlined"
                          size="small"
                          sx={{ minWidth: 150, bgcolor: 'white' }}
                          SelectProps={{ native: true }}
                        >
                          <option value="">Select Bloom Level</option>
                          <option value="Remember">Remember</option>
                          <option value="Understand">Understand</option>
                          <option value="Apply">Apply</option>
                          <option value="Analyze">Analyze</option>
                          <option value="Evaluate">Evaluate</option>
                          <option value="Create">Create</option>
                        </TextField>
                      ) : (
                        outcome.bloom_level && (
                          <Chip 
                            label={`Bloom: ${outcome.bloom_level}`} 
                            size="small" 
                            sx={{ width: 'fit-content', bgcolor: (BLOOM_COLORS[outcome.bloom_level] || '#9ca3af') + '30' }} 
                          />
                        )
                      )}
                      {editingField !== `bloom_level_${globalIdx}` && (
                        <IconButton
                          size="small"
                          onClick={() => startEdit(`bloom_level_${globalIdx}`, outcome.bloom_level || '')}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      )}
                      {editingField === `bloom_level_${globalIdx}` && (
                        <>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={() => saveEdit(globalIdx, 'bloom_level', editedValue)}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={cancelEdit}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </Box>
                    {outcome.assessment_criteria && outcome.assessment_criteria.length > 0 && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#64748b' }}>
                            Assessment Criteria:
                          </Typography>
                        </Box>
                        {outcome.assessment_criteria.map((criteria, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            {editingField === `assessment_criteria_${globalIdx}_${i}` ? (
                              <>
                                <TextField
                                  fullWidth
                                  value={editedValue}
                                  onChange={(e) => setEditedValue(e.target.value)}
                                  variant="outlined"
                                  size="small"
                                  sx={{ bgcolor: 'white' }}
                                  autoFocus
                                />
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  startIcon={<SaveIcon />}
                                  onClick={() => saveEdit(globalIdx, `assessment_criteria_${i}`, editedValue)}
                                >
                                  Save
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<CancelIcon />}
                                  onClick={cancelEdit}
                                >
                                  Cancel
                                </Button>
                              </>
                            ) : (
                              <>
                                <Typography variant="body2" sx={{ fontSize: '0.85rem', flex: 1 }}>
                                  • {criteria}
                                </Typography>
                                <Box
                                  component="div"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEdit(`assessment_criteria_${globalIdx}_${i}`, criteria);
                                  }}
                                  sx={{ 
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 32,
                                    height: 32,
                                    cursor: 'pointer',
                                    borderRadius: '50%',
                                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                                    color: 'inherit',
                                    ml: 1
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </Box>
                              </>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
